import React, { useMemo } from "react";
import { Checkbox, FormControlLabel, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DialogField from "modules/common/components/dialogField";
import { MAX_SHORTCUT_NAME_LENGTH, ShortcutGroup, ShortcutUpdateModelv1 } from "modules/shortcuts/models";
import ShortcutGroupSearch from "./shortcutGroupSearch";
import HoverText from "modules/documents/components/action-buttons/hoverText";
import { Audience } from "modules/audiences";
import ChipList from "modules/common/components/chipList";
import { isUrlValid } from "utils/isUrlValid";
import TextFieldWithInputCounter from "modules/common/components/forms/inputs/textFieldWithInputCounter";
import AddIcon from "@mui/icons-material/Add";
import AudienceSearchCallout from "modules/common/components/audienceSearchCallout";
import { useSelector } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

import "../../styles/shortcutDialogBody.sass";
interface IShortcutDialogBodyProps {
    input: ShortcutUpdateModelv1;
    setInput: React.Dispatch<React.SetStateAction<ShortcutUpdateModelv1>>;
    availableAudiences: Audience[];
}

const ShortcutDialogBody: React.FC<IShortcutDialogBodyProps> = ({
    input,
    setInput,
    availableAudiences
}) => {
    const [innerUrlVal, setInnerUrlVal] = React.useState<string>(input.url);
    const allShortcutGroups = useSelector((state: GlobalApplicationState) => state.shortcuts.originalShortcutFormat.flatMap(f => f.shortcutGroup));
    
    const selectedShortcutGroup = React.useMemo(
        () => allShortcutGroups.find(g => g.id === input.groupId), 
        [input.groupId, allShortcutGroups]
    );

    const inputAudienceSimpleTags = useMemo(
        () => input.audiences.map(aId => ({ id: aId, name: availableAudiences.find(a => a.id === aId)?.displayName ?? "" })), 
        [input.audiences, availableAudiences]
    );

    const getNameField = () => 
        <TextFieldWithInputCounter
            value={input.name}
            onChange={(e) => {
                e.persist();
                setInput(prev => ({ ...prev, name: e.target.value }));
            }}
            variant="outlined"
            size="small"
            fullWidth
            multiline={false}
            inputProps={{ style: { fontSize: '1rem' }}}
            style={{ minHeight: 40 }}
            maxCount={MAX_SHORTCUT_NAME_LENGTH}
        />;

    const onChangeShortcutGroupValue = (newGroup: ShortcutGroup | null) => {
        setInput(prev => ({ ...prev, groupId: newGroup?.id }));
    }

    const onBlurUrl = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
        let urlVal = innerUrlVal;

        if (urlVal && !urlVal.startsWith("http") && !isUrlValid(urlVal)) {
            urlVal = `https://${urlVal}`;
        }

        setInnerUrlVal(urlVal);
        setInput(prev => ({ ...prev, url: urlVal }));
    }

    const getUrlField = (): JSX.Element =>
        <>
            <TextField
                value={innerUrlVal}
                error={input.url !== "" && !isUrlValid(input.url)}
                onChange={(e) => setInnerUrlVal(e.target.value)}
                onBlur={onBlurUrl}
                size="small"
                variant="outlined"
                InputProps={{
                    endAdornment: input.url && 
                    <InputAdornment position="end">
                        <IconButton onClick={() => window.open(input.url, "_blank")}>
                            <OpenInNewIcon/>
                        </IconButton>
                    </InputAdornment>
                }}
                inputProps={{
                    style: {
                        fontSize: '1rem'
                    }
                }}
                style={{ minHeight: 40 }}
                fullWidth
            />
            <FormControlLabel 
                control={
                    <Checkbox 
                        defaultChecked={input.openInNewWindow} 
                        onChange={(_, checked) => setInput(prev => ({...prev, openInNewWindow: checked}))}
                    />
                } 
                label="Open in new tab"
                className="open-in-new-tab-label"
            />
        </>;

    const getAudienceField = (): JSX.Element => 
        <div className="shortcuts-audience-field">
            <AudienceSearchCallout
                availableAudienceIdNames={availableAudiences.map(a => ({ id: a.id, name: a.displayName }))}
                selectedOptions={inputAudienceSimpleTags}
                onAudienceFilterChange={aud => setInput(prev => ({ ...prev, audiences: aud.map(a => a.id) }))}
                buttonStyle={{
                    buttonClassName: "shortcuts-edit-audiences-button",
                    buttonText: "Edit audiences",
                    startIcon: <AddIcon />,
                }}
            />
            <div>
                <ChipList
                    items={inputAudienceSimpleTags}
                    emptyChipDetails={{
                        show: true,
                        contents: "All users"
                    }}
                    closable
                    onClose={aud => setInput(prev => ({...prev, audiences: prev.audiences.filter(id => id !== aud.id)}))}
                    truncateAtIndex={4}
                />
            </div>
        </div>;

    const getAudienceFieldNameComponent = (): JSX.Element => 
        <HoverText
            label="Audiences"
            labelContainerClassName="audience-label"
        >
            Select audiences to make this shortcut private to specific employees.
        </HoverText>;
    
    return <React.Fragment>
        <Grid 
            container
            alignItems={"center"}
            spacing={1}
            className="shortcut-dialog-body-grid-container"
        >
            <DialogField
                fieldName="Shortcut name"
                fieldInput={getNameField()}
                mandatory
                fieldLabelGridClassName="name-label-grid-item"
            />
            <DialogField
                fieldName="Shortcut group"
                fieldInput={
                    <ShortcutGroupSearch
                        shortcutGroupValue={selectedShortcutGroup}
                        onChangeShortcutGroupValue={onChangeShortcutGroupValue}
                    />
                }
                mandatory
            />
            <DialogField
                fieldName="Link to"
                fieldInput={getUrlField()}
                mandatory
                fieldLabelGridClassName="link-to-label-grid-item"
            />
            <DialogField
                fieldName={getAudienceFieldNameComponent()}
                fieldInput={getAudienceField()}
                fieldLabelGridClassName="audience-label-grid-item"
            />
        </Grid>
    </React.Fragment>;
};

export default ShortcutDialogBody;
